import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Card, Container } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import FormGenerator from '../../form-generator/FormGenerator';
import { setAttribute } from '../../form-generator/helpers/utility';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import { processTasksForSubmission } from '../../helpers/taskHelpers';
import {
  addDocHubFormFieldsToForm,
  getObjectForPrefill
} from '../../helpers/formHelpers';
import { openLinkInNewTab } from '../../helpers/global';
import NotFound from '../NotFound';
import SubmitButtonRow from '../common/SubmitButtonRow';
import File from '../common/file';
import ContractedUnitOverview from '../inventory/ContractedUnitOverview';
import InventoryPicker from '../inventory/InventoryPicker';
import Tasks from '../tasks/Tasks';
import { addChoicesToContractForm, formJson, validateAndCleanupForm } from './form';
import { getProfileFromLocalStorage } from '../../helpers/session';
import { setupContactOwnerDropdown } from '../../helpers/formHelpers';
import { getUserRoles, isAdmin } from '../../helpers/roleHelpers';

const CreateForm = ({
  appChoices = {},
  onContractAdd,
  prefillContract,
  onAddInventoryPopupMetaChange,
  addInvenroryPopupMeta,
  onAddInvetoryPopupMetaClose
}) => {
  const location = useLocation();
  const [form, setForm] = useState(null);
  const [zipCodes, setZipCodes] = useState([]);
  const [importingContact, setImportingContact] = useState(
    prefillContract ? getObjectForPrefill(prefillContract) : undefined
  );

  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [goToDochub, setGoToDocHub] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [inventoryPopupMeta, setInventoryPopupMeta] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [tasks, setTasks] = useState([]);
  const tasksRef = useRef([]);
  const tasksValidationRef = useRef(null);
  const [expandRequest, setExpandRequest] = useState(null);
  const currentUserId = useMemo(() => getProfileFromLocalStorage()?._id || null, []);

  const setUpForm = () => {
    const { preFillValues, inventoryUnit } = location.state || {};
    if (preFillValues) {
      setImportingContact(getObjectForPrefill(preFillValues));
      setSelectedInventory(inventoryUnit);
      if (preFillValues.tasks) {
        setTasks(preFillValues.tasks);
        tasksRef.current = preFillValues.tasks;
      }
      window.history.replaceState({}, document.title);
    }
    const newFormJson = addDocHubFormFieldsToForm({ formJson: formJson(getUserRoles()), appChoices });
    addChoicesToContractForm({ appChoices, form: newFormJson, initialFormValues: preFillValues });
    setZipCodes(appChoices?.find(a => a?.key === 'zipCodes')?.['values']);
    setupContactOwnerDropdown({ appChoices, formJson: newFormJson });
    setAttribute(newFormJson, 'lender', 'required', Boolean(inventoryUnit));
    setForm(newFormJson);
  };

  useEffect(() => {
    if (!isAdmin()) return;
    setUpForm();
  }, []);

  if (!isAdmin()) {
    return <NotFound text="You are not authorized to view this page" />;
  }

  const onFormSubmit = async form => {
    if (uploadedFiles.some(f => f.status === 'ERROR')) {
      return toast.error('One or more files have failed uploading, please discard these files and try again!');
    }

    if (uploadedFiles.some(f => f.status !== 'UPLOADED')) {
      return toast.error('Some of the files are still uploading to the server, please try again in a moment!');
    }

    if (selectedInventory && !form.lender) {
      toast.error('Lender is a required field');
      setExpandRequest({ fieldId: 'lender', message: 'Lender is required' });
      return;
    }

    //checks
    const valid = validateAndCleanupForm(form);
    if (!valid) return;

    if (tasksValidationRef.current && !tasksValidationRef.current()) return;

    form.files = uploadedFiles.map(f => f.id);
    if (selectedInventory) form.inventoryUnit = selectedInventory._id;

    if (importingContact && !importingContact.hasOwnProperty('fromContract')) {
      form.importingContact = importingContact._id;
      if (importingContact.webhookFormData) form.webhookFormData = importingContact.webhookFormData;
    }

    // Process tasks - always send array (empty if no tasks) to distinguish from "not provided"
    const processedTasks = processTasksForSubmission(tasksRef.current, currentUserId, false, false);
    form.tasks = processedTasks !== undefined ? processedTasks : [];

    toast.info('Processing contract. This may take a while if Calendar events and email attachments are involved.');

    form.specialInstructions = form.specialInstructions.flatMap(i => i);
    setButtonsDisabled(true);

    const { response, error } = await makeApiRequests({
      requestBody: form,
      endpoint: ENDPOINTS.CONTRACTS_BASE
    });
    setButtonsDisabled(false);

    if (error) {
      return toast.error(error);
    }

    toast(`Contract created successfully!`, {
      type: 'success'
    });

    setUploadedFiles([]);

    if (onContractAdd) {
      onContractAdd(response);
    }

    setTimeout(() => {
      if (goToDochub) {
        openLinkInNewTab(`/dochub/print-groups?contractId=${response._id}`);
      }
      //this means it is not from a popup or sidebar
      if (!onContractAdd) document.location.reload();
    }, 1000);
  };

  const autoFillAddress = e => {
    try {
      const zipCode = e ? e.target.value : document.getElementById('zipCode').value;
      const zipCodeFiltered = zipCodes.filter(zip => zipCode == zip['Zip Code']);

      if (zipCodeFiltered.length > 0) {
        document.getElementById('propertyState').value = zipCodeFiltered[0]['Property State'];
        document.getElementById('propertyCity').value = zipCodeFiltered[0]['Property City'];
        document.getElementById('propertyCounty').value = zipCodeFiltered[0]['Property County'];
      }
    } catch (e) { }
  };

  const autoFillMailingAddress = e => {
    try {
      const zipCode = e ? e.target.value : document.getElementById('mailingZip').value;
      const zipCodeFiltered = zipCodes.filter(zip => zipCode == zip['Zip Code']);

      if (zipCodeFiltered.length > 0) {
        document.getElementById('mailingState').value = zipCodeFiltered[0]['Property State'];
        document.getElementById('mailingCity').value = zipCodeFiltered[0]['Property City'];
        document.getElementById('mailingCounty').value = zipCodeFiltered[0]['Property County'];
      }
    } catch (e) { }
  };

  window['autoFillMailingAddress'] = autoFillMailingAddress;
  window['autoFillAddress'] = autoFillAddress;
  window['onFormSubmit'] = onFormSubmit;

  const onButtonClick = goToDochub => {
    setGoToDocHub(goToDochub);
    setTimeout(() => {
      document.getElementById('add').click();
    }, 100);
  };

  const onLinkUnitClick = () => {
    setInventoryPopupMeta({});
  };

  const updateLenderRequired = required => {
    setAttribute(form, 'lender', 'required', required);
    setForm({ ...form });
  };

  const onRemoveUnitClick = () => {
    setSelectedInventory(null);
    updateLenderRequired(false);
  };

  const onInventorySubmit = inventory => {
    setSelectedInventory(inventory[0]);
    updateLenderRequired(true);
    setInventoryPopupMeta(null);
  };

  const onUnitSelect = unit => {
    setInventoryPopupMeta({ ...inventoryPopupMeta, selectedUnit: unit });
  };

  return (
    <Container fluid className="py-4 px-md-3">
      <Card>
        <Card.Body className="fade-in">
          {form && (
            <>
              <SubmitButtonRow
                disabled={buttonsDisabled}
                showGoToDocHub
                onGoToDocHubClick={() => onButtonClick(true)}
                onSaveClick={() => onButtonClick(false)}
              />
              <FormGenerator
                formJson={form}
                formValues={
                  importingContact
                    ? { add: importingContact }
                    : { add: { amountACRebateReceived: '500' } }
                }
                expandRequest={expandRequest}
              />
              <File uploadedFiles={uploadedFiles} onUploadedFilesChange={setUploadedFiles} containerClassName="mx-3" />

              <div className="m-3">
                <ContractedUnitOverview
                  onLinkUnitClick={onLinkUnitClick}
                  onRemoveUnitClick={onRemoveUnitClick}
                  onChangeUnitClick={onLinkUnitClick}
                  inventoryUnit={selectedInventory}
                />
                <InventoryPicker
                  excludedUnitIds={selectedInventory ? [selectedInventory['ID']] : []}
                  selectedUnits={
                    inventoryPopupMeta && inventoryPopupMeta.selectedUnit ? [inventoryPopupMeta.selectedUnit] : []
                  }
                  onUnitSelect={onUnitSelect}
                  show={inventoryPopupMeta}
                  onSubmit={onInventorySubmit}
                  fromContracts
                  onInventoryPickerClose={() => setInventoryPopupMeta(null)}
                  onAddInventoryPopupMetaChange={onAddInventoryPopupMetaChange}
                  addInvenroryPopupMeta={addInvenroryPopupMeta}
                  onAddInvetoryPopupMetaClose={onAddInvetoryPopupMetaClose}
                />
                {importingContact && !importingContact.hasOwnProperty('fromContract') && (
                  <h6 className="midFont text-right text-muted  mt-3 mb-0">
                    <i>
                      You are importing contact <b className="text-dark">{importingContact['buyer']}</b>. Saving this
                      contract will disable the contact and make it unavailable for edit.
                    </i>
                  </h6>
                )}
              </div>
              <div className="m-3">
                <div style={{ width: '50%' }}>
                  <Tasks
                    tasks={tasks}
                    disabled={false}
                    onTasksChange={(updatedTasks) => {
                      setTasks(updatedTasks);
                      tasksRef.current = updatedTasks;
                    }}
                    appChoices={appChoices}
                    currentUserId={currentUserId}
                    onValidationRef={tasksValidationRef}
                  />
                </div>
              </div>
              <SubmitButtonRow
                disabled={buttonsDisabled}
                showGoToDocHub
                onGoToDocHubClick={() => onButtonClick(true)}
                onSaveClick={() => onButtonClick(false)}
                bottom
              />
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CreateForm;
